import _ from "lodash";
import {
  FIRST_NAME,
  NAME_MAPPING,
  DEFAULT_NAME_ORDER_PREFERENCE,
} from "Constants/stringConstants";
import {
  getFormattedStudentName,
  getStudentIdentifierName,
  getStudentOrderBy,
  getDefaultStudentNameOrderPreference,
} from "./GetFormattedStudentName";
import {
  getFormattedStaffNameV2,
  getStaffIdentifierName,
  getStaffOrderBy,
  getStaffSortByList,
} from "./GetFormattedStaffName";
import {
  getFormattedFamilyMemberName,
  getFamilyMemberIdentifierName,
} from "./GetFormattedFamilyMemberName";
import { getStudentSortByList } from "./GetStudentSortByList";
import { getCleanNameOption } from "./GetCleanNameOption";

/**
 * Get the formatted name of a user based on their type.
 * @param {object} user user object containing user details
 * @param {string} type type of user (e.g., "student", "staff", "parent").
 * @param {string} rest Additional options to pass for student i.e showStudentUID, orderBy, showAdditionalInfo, t
 * @returns {string} - Formatted user name.
 */
export const getFormattedUserName = ({ user, type, ...rest }) => {
  const userObjType = _.get(user, "type", "");
  const { firstName = "", lastName = "" } = user || {};
  const userType = type || userObjType;
  switch (userType) {
    case "student":
      return getFormattedStudentName({
        student: user,
        ...rest,
      });
    case "staff":
      return getFormattedStaffNameV2({
        staff: user,
        ...rest,
      });
    case "parent":
      return getFormattedFamilyMemberName({
        familyMember: user,
        ...rest,
      });
    default:
      return `${firstName} ${lastName}`;
  }
};

/**
 * Get the user identifier name of a user based on their type.
 * @param {object} user user object containing user details
 * @param {string} type type of user (e.g., "student", "staff", "parent").
 * @returns {string} - Formatted user identifier.
 */
export const getUserIdentifierName = ({ user, type, isVisitor = false }) => {
  const userObjType = _.get(user, "type", "");
  const { firstName = "" } = user || {};
  const userType = type || userObjType;
  switch (userType) {
    case "student":
      return getStudentIdentifierName({
        student: user,
      });
    case "staff":
      return getStaffIdentifierName({
        staff: user,
        isVisitor,
      });
    case "parent":
      return getFamilyMemberIdentifierName({
        familyMember: user,
      });
    default:
      return firstName;
  }
};

export const getDefaultOrderBy = ({ type, isVisitor = false }) => {
  switch (type) {
    case "student":
      return getStudentOrderBy();
    case "staff":
      return getStaffOrderBy({ isVisitor });
    default:
      return FIRST_NAME;
  }
};

/**
 * Get the sort options of a user based on their type.
 * @param {Array} sortOptions user array containing user sort order
 * @param {string} type type of user (e.g., "student", "staff", "parent").
 * @param {string} keyToMap map key to sortBy i.e "sortEntity".
 * @returns {Array} - sort option array.
 */
export const getUserSortOptions = ({
  type,
  sortOptions,
  keyToMap,
  isVistor = false,
}) => {
  switch (type) {
    case "student":
      return getStudentSortByList({ sortList: sortOptions, keyToMap });
    case "staff":
      return getStaffSortByList({ sortOptions, keyToMap, isVistor });
    default:
      return sortOptions;
  }
};

export const clearNameSettingCache = () => {
  getDefaultStudentNameOrderPreference.cache.clear();
};

export const getNameStringUsingNameOrder = ({
  userNameOrder = DEFAULT_NAME_ORDER_PREFERENCE,
  user,
}) => {
  let fullName = "";

  _.forEach(userNameOrder, order => {
    const isInitial = _.includes(order, "_INITIAL");
    const cleanOrder = getCleanNameOption(order, true);

    const propertyName = NAME_MAPPING[cleanOrder];
    const propertyValue = _.get(user, propertyName, "");

    if (propertyValue) {
      if (fullName) {
        fullName = fullName + " ";
      }
      if (isInitial) {
        const cleanOrderWithInitial = getCleanNameOption(order);
        fullName += _.replace(
          order,
          cleanOrderWithInitial,
          _.get(propertyValue, "[0]", "")
        );
      } else {
        fullName += _.replace(order, cleanOrder, propertyValue);
      }
    }
  });
  return fullName;
};
